import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_SECTION, isSectionId, type SectionId } from './sections';

/** `#/spaces/abc?x=1` → `['spaces', 'abc']`. */
function readSegments(): string[] {
  const raw = window.location.hash.replace(/^#\/?/, '').split('?')[0] ?? '';

  return raw
    .split('/')
    .filter((part) => part !== '')
    .map((part) => decodeURIComponent(part));
}

function readSectionFromHash(): SectionId {
  const first = readSegments()[0] ?? '';
  return isSectionId(first) ? first : DEFAULT_SECTION;
}

/**
 * Minimal hash-based section navigator. The spec forbids adding a routing
 * runtime dependency for the MVP, so navigation state lives in the URL hash
 * and stays shareable, bookmarkable, and back-button friendly.
 *
 * Only the FIRST path segment names the section; later segments address a
 * record inside it (see `useDetailRoute`).
 */
export function useSectionRoute(): {
  section: SectionId;
  navigate: (next: SectionId) => void;
} {
  const [section, setSection] = useState<SectionId>(readSectionFromHash);

  useEffect(() => {
    const onHashChange = (): void => {
      setSection(readSectionFromHash());
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  const navigate = useCallback((next: SectionId) => {
    window.location.hash = `#/${next}`;
    setSection(next);
  }, []);

  return { section, navigate };
}

/**
 * The record a section is currently drilled into — `#/spaces/<id>` opens that
 * space's own page. Kept in the hash rather than in component state so the
 * browser's back button leaves the detail page, and so a page can be linked.
 */
export function useDetailRoute(section: SectionId): {
  detailId: string | undefined;
  open: (id: string) => void;
  close: () => void;
} {
  const read = useCallback((): string | undefined => {
    const segments = readSegments();
    return segments[0] === section ? segments[1] : undefined;
  }, [section]);

  const [detailId, setDetailId] = useState<string | undefined>(read);

  useEffect(() => {
    const onHashChange = (): void => {
      setDetailId(read());
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  }, [read]);

  const open = useCallback(
    (id: string) => {
      window.location.hash = `#/${section}/${encodeURIComponent(id)}`;
      setDetailId(id);
    },
    [section],
  );

  const close = useCallback(() => {
    window.location.hash = `#/${section}`;
    setDetailId(undefined);
  }, [section]);

  return { detailId, open, close };
}
